"use client";

import { useEffect, useState } from "react";
import { useSession } from "next-auth/react";
import { useMessaging } from "@/contexts/messaging-context";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MessageSquare } from "lucide-react";
import { getPusherClient } from "@/lib/pusher-client";
import { getUnreadCount } from "@/app/actions/messaging";
import { toast } from "sonner";

export function MessageNotificationBadge() {
  const { data: session, status } = useSession();
  const {
    openWidget,
    openModal,
    isWidgetOpen,
    activeConversationId,
    totalUnreadCount,
    fetchUnreadCount
  } = useMessaging();
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastConversationId, setLastConversationId] = useState<string | null>(null);

  useEffect(() => {
    if (status === "authenticated" && session?.user?.id) {
      // Fetch initial unread count
      fetchUnreadCount();

      // Subscribe to real-time unread count updates
      const pusher = getPusherClient();
      const channel = pusher.subscribe(`user-${session.user.id}`);

      channel.bind("unread-count", (data: { unreadCount: number }) => {
        setUnreadCount(data.unreadCount);

        // Show toast notification for new message
        if (data.unreadCount > unreadCount) {
          toast.info("New message received", {
            action: {
              label: "View",
              onClick: handleClick,
            },
          });
        }
      });

      // Track last conversation with new message
      channel.bind("new-message", (data: { conversationId: string }) => {
        setLastConversationId(data.conversationId);
      });

      return () => {
        channel.unbind_all();
        channel.unsubscribe();
      };
    }
  }, [session, status, unreadCount]);

  function handleClick() {
    // Always open modal instead of widget
    // If there's a last conversation with new message, open that one
    // Otherwise just open the modal to show all conversations
    openModal(lastConversationId || undefined);
  }

  if (status !== "authenticated") {
    return null;
  }

  // Use context unread count if available, fallback to local state
  const displayCount = totalUnreadCount || unreadCount;

  return (
    <Button
      variant="ghost"
      size="icon"
      className="relative h-10 w-10 hover:bg-accent/50 sidebar-hover"
      onClick={handleClick}
      data-tour="header-messages"
    >
      <MessageSquare className="h-5 w-5 text-orange-500" />
      {displayCount > 0 && (
        <Badge
          variant="destructive"
          className="absolute -top-1 -right-1 h-5 min-w-[20px] rounded-full px-1 text-xs"
        >
          {displayCount > 99 ? "99+" : displayCount}
        </Badge>
      )}
    </Button>
  );
}
